import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { requestFormById } from 'api/operations/formService';
import {
  completeSubscriberChecklistStep,
  getIMQRequiredSubInfo,
  getIMQSubscriberById,
} from 'api/operations/electionService';
import { SubscriberDomainParams } from 'global/hooks/useSubscriberDomainParams';
import { getRootStoreInstance } from 'store/rootStore';
import EmployeeFlowDelegate from 'store/flows/subscriber/employeeFlowDelegate';
import { SubChecklistCard } from 'types/electionService';
import { ChecklistCompletionStatus, ChecklistOption } from 'types/enrollmentService';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import { SubscriberRouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';

export default class IMQSubscriberProfilePageStore {
  private readonly _rootStore = getRootStoreInstance();
  private readonly _domainParams: SubscriberDomainParams;
  private _subscriberDelegate: SubscriberDelegate | null = null;
  public readonly flowDelegate: EmployeeFlowDelegate;

  public dynamicFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: this._rootStore.userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> => requestFormById(formId),
        onSubmit: this.updatePrimaryInfo.bind(this),
        disableResetAfterSubmit: true,
      }),
  );

  public get domainParams(): SubscriberDomainParams {
    return this._domainParams;
  }

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isInitialDataLoading;
  }

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this._subscriberDelegate?.getTaskChecklistOptions(this._domainParams.entityId) || [];
  }

  public get isStepCompleted(): boolean {
    return (
      this.checklistOptions.find((option) => option.card === SubChecklistCard.IMQ_SUBSCRIBER_PROFILE)
        ?.completionStatus === ChecklistCompletionStatus.COMPLETED
    );
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getIMQSubscriberById(this._domainParams.subscriberId);
    runInAction(() => {
      this._subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainParams.entityId });
    });
  }

  public async getInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);

    const domainId = this._rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getIMQRequiredSubInfo(domainId);

    await this._getSubscriber();

    await runInAction(async () => {
      const formId = requiredSubInfo.imq.subFormId;
      if (!formId) {
        throw new Error('No subscriber profile form configured for IMQ');
      }

      await this.dynamicFormDelegate.buildForm(formId, this._subscriberDelegate?.subscriber.common || {});
      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async updatePrimaryInfo(): Promise<void> {
    if (!this.dynamicFormDelegate.isFormValid) return;

    await this._subscriberDelegate?.updatePrimary(this.dynamicFormDelegate.formAnswers, this._domainParams.entityId);
    await runInAction(async () => {
      await completeSubscriberChecklistStep({
        subId: this._domainParams.subscriberId,
        entityId: this._domainParams.entityId,
        card: SubChecklistCard.IMQ_SUBSCRIBER_PROFILE,
      });
      this.flowDelegate.onCompleteRedirect();
    });
  }

  public returnBack(): void {
    redirectToRoute(SubscriberRouteV2Id.EMPLOYEE_PROFILE, { groupId: this._domainParams.groupId });
  }

  constructor(domainParams: SubscriberDomainParams) {
    this._domainParams = domainParams;
    this.flowDelegate = new EmployeeFlowDelegate({ domainParams });

    makeAutoObservable(
      this,
      {
        flowDelegate: false,
        dynamicFormDelegate: false,
      },
      { autoBind: true },
    );
  }
}

const IMQSubscriberProfilePageStoreContext = createContext(
  new IMQSubscriberProfilePageStore({ groupId: '', subscriberId: '', entityId: '' }),
);

export const useIMQSubscriberProfilePageStore = (): IMQSubscriberProfilePageStore => {
  return useContext(IMQSubscriberProfilePageStoreContext);
};

export { IMQSubscriberProfilePageStoreContext };
